/**
 * AdoptSignatureModal — DocuSign/Adobe-style "adopt your signature" overlay for
 * the signer kiosk (Bill 2026-07-08).
 *
 * The signer adopts ONE signature per session (per signer): either DRAW it on a
 * touch canvas, or TYPE their legal name and pick a cursive STYLE. The adopted
 * signature is held in kiosk session state (SignerKiosk) and re-applied at every
 * subsequent signature spot with a single tap — the signer never re-draws or
 * re-types.
 *
 * DRAW reuses the shared DrawnSignatureCapture primitive verbatim. TYPE renders
 * the legal name in the bundled cursive faces (Allura / Dancing Script — shipped
 * by @retail-ops/signing-capture's @fontsource imports, which land in this
 * bundle via SignatureCaptureRouter → TypedSignatureCapture; NO CDN) and lets
 * the signer choose the style.
 *
 * The emitted AdoptedSignature carries BOTH a raster (pngBase64) and a vector
 * (svg) AND the typed name, so ANY later step captureMode ('drawn' | 'typed')
 * can be served from a single adoption without re-prompting the signer — see
 * SignerKiosk.signWithAdopted().
 */

import { useMemo, useState, type CSSProperties } from 'react';
import { DrawnSignatureCapture } from '@retail-ops/signing-capture';
import type { SignatureCaptureResult } from '@retail-ops/shared-types';

// ── Adopted-signature shape (kiosk session state) ────────────────────────────

export type AdoptMode = 'drawn' | 'typed';

export interface AdoptedSignature {
  mode: AdoptMode;
  /** Always populated — typed legal name, or the signer's legal name for draw. */
  typedName: string;
  /** Typed style id (undefined for a drawn signature). */
  style?: string;
  /** Always populated — drawn svgPath OR the typed cursive renderedSvg. */
  svg: string;
  /** Always populated — drawn canvas PNG OR the rasterized typed cursive PNG. */
  pngBase64: string;
}

// ── Typed cursive styles (bundled faces — no CDN) ────────────────────────────

interface TypedStyle {
  id: string;
  /** ctx.font / CSS font-family stack (primary face first, cursive fallbacks). */
  font: string;
  /** SVG font-family attribute value. */
  svgStack: string;
}

/**
 * Typed signature styles.
 *
 * Bill 2026-07-16: the previous two options — Allura and Dancing Script — are BOTH
 * flowing connected calligraphic scripts. Side by side at signature size they read as
 * the same handwriting, so "choose your style" offered a choice the signer couldn't
 * actually see. Ours vs DocuSign's adopt dialog (docs/DocuSignSignatureStyleAdoption.png)
 * is the whole point: their list is obviously different hands.
 *
 * So these are picked for VISUAL DISTANCE, not for being pretty together — a formal
 * copperplate, a bouncy script, a ballpoint hand, a fat brush, and a print-ish hand.
 * A signer should be able to tell them apart in one glance at a kiosk, at arm's length.
 *
 * All bundled locally via @fontsource (real .woff2 on disk — NO CDN, workspace rule).
 * The @fontsource imports live in @retail-ops/signing-capture so both the ROP kiosk and
 * this portal get the faces from one place; adding a style here without the matching
 * import there silently falls back to the generic cursive and every option looks the
 * same again — which is exactly the bug being fixed.
 *
 * Licenses: Allura / Dancing Script / Caveat / Satisfy / Kalam = SIL OFL 1.1;
 * Homemade Apple = Apache 2.0. All fine for commercial use.
 */
const TYPED_STYLES: ReadonlyArray<TypedStyle> = [
  {
    // Formal copperplate — thin, high-contrast, strongly connected.
    id: 'allura',
    font: `"Allura", "Snell Roundhand", "Brush Script MT", cursive`,
    svgStack: `Allura, 'Snell Roundhand', 'Brush Script MT', cursive`,
  },
  {
    // Bouncy modern script — heavier stroke, lively baseline.
    id: 'dancing',
    font: `"Dancing Script", "Snell Roundhand", "Brush Script MT", cursive`,
    svgStack: `'Dancing Script', 'Snell Roundhand', 'Brush Script MT', cursive`,
  },
  {
    // Ballpoint hand — looks like a real person signed a real page.
    id: 'homemade',
    font: `"Homemade Apple", "Segoe Script", cursive`,
    svgStack: `'Homemade Apple', 'Segoe Script', cursive`,
  },
  {
    // Fat brush — thick, casual, unmistakable next to the copperplate.
    id: 'satisfy',
    font: `"Satisfy", "Brush Script MT", cursive`,
    svgStack: `Satisfy, 'Brush Script MT', cursive`,
  },
  {
    // Print-ish hand — barely joined, for signers who don't write cursive.
    id: 'kalam',
    font: `"Kalam", "Segoe Print", "Comic Sans MS", cursive`,
    svgStack: `Kalam, 'Segoe Print', cursive`,
  },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Portable SVG the server can re-render verbatim (mirrors the typed primitive). */
function buildTypedSvg(text: string, fontStack: string): string {
  const safe = text.replace(/[&<>]/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'
  );
  const estWidth = Math.max(360, safe.length * 30);
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${estWidth}" height="96" viewBox="0 0 ${estWidth} 96">`,
    `<text x="50%" y="66" text-anchor="middle" font-family="${fontStack}" font-style="italic" font-size="60" fill="#1a406a">`,
    safe,
    `</text>`,
    `</svg>`,
  ].join('');
}

/**
 * Rasterize the typed cursive name to a PNG (no data: prefix). Used so a TYPED
 * adoption can still satisfy a later 'drawn' step, which needs canvasPngBase64.
 * Loads the chosen face first so the raster matches the on-screen preview.
 */
async function rasterizeTypedPng(text: string, fontStack: string): Promise<string> {
  const width = Math.max(360, text.length * 34);
  const height = 120;
  const canvas = document.createElement('canvas');
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.scale(dpr, dpr);
  const fontSpec = `italic 400 64px ${fontStack}`;
  try {
    if (document.fonts && typeof document.fonts.load === 'function') {
      await document.fonts.load(fontSpec, text);
    }
  } catch {
    /* fall through — the cursive fallback face still renders */
  }
  ctx.font = fontSpec;
  ctx.fillStyle = '#1a406a';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, width / 2, height / 2);
  const url = canvas.toDataURL('image/png');
  return url.replace(/^data:image\/png;base64,/, '');
}

// ── Styles (kiosk conventions — light surface, PCB brand) ────────────────────

const FONT = "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif";
const overlayS: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  overflow: 'auto', fontFamily: FONT,
};
const cardS: CSSProperties = {
  background: '#fff', color: '#0f172a', borderRadius: 14, width: '100%', maxWidth: 720,
  boxShadow: '0 24px 60px rgba(0,0,0,0.32)', display: 'flex', flexDirection: 'column',
};
const headS: CSSProperties = { padding: '20px 26px 0' };
const titleS: CSSProperties = { margin: 0, fontSize: 21, fontWeight: 700, color: '#1a406a' };
const subS: CSSProperties = { margin: '6px 0 0', fontSize: 14, color: '#475569', lineHeight: 1.5 };
const tabsRowS: CSSProperties = { display: 'flex', gap: 8, padding: '16px 26px 0' };
const tabBtnS = (active: boolean): CSSProperties => ({
  flex: 1, padding: '10px 14px', borderRadius: 8, fontSize: 15, fontWeight: 600,
  cursor: 'pointer', border: `1px solid ${active ? '#2968ad' : '#cbd5e1'}`,
  background: active ? '#2968ad' : '#fff', color: active ? '#fff' : '#1f2937',
});
const bodyPadS: CSSProperties = { padding: '16px 26px 0' };
const fieldLabelS: CSSProperties = { fontSize: 13, fontWeight: 600, color: '#1f2937', display: 'block', marginBottom: 6 };
const inputS: CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: 16, fontFamily: 'inherit', boxSizing: 'border-box',
};
const styleGridS: CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 };
const styleCardS = (active: boolean): CSSProperties => ({
  border: `2px solid ${active ? '#2968ad' : '#e2e8f0'}`, borderRadius: 10,
  background: active ? '#eef4fb' : '#f8fafc', padding: '10px 12px 12px', cursor: 'pointer',
  display: 'flex', flexDirection: 'column', gap: 6, minHeight: 96, position: 'relative',
});
const stylePreviewS = (fontStack: string): CSSProperties => ({
  fontFamily: fontStack, fontStyle: 'italic', fontSize: 40, color: '#1a406a',
  lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  flex: 1, display: 'flex', alignItems: 'center',
});
const styleTagS = (active: boolean): CSSProperties => ({
  fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
  color: active ? '#2968ad' : '#94a3b8',
});
const footerS: CSSProperties = { padding: '18px 26px 22px', display: 'flex', justifyContent: 'flex-end', gap: 10 };
const ghostBtnS: CSSProperties = {
  padding: '11px 18px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff',
  color: '#1f2937', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const primaryBtnS = (enabled: boolean): CSSProperties => ({
  padding: '11px 22px', borderRadius: 8, fontSize: 15, fontWeight: 700, color: '#fff',
  background: enabled ? '#386732' : '#9ca3af', border: `1px solid ${enabled ? '#386732' : '#9ca3af'}`,
  cursor: enabled ? 'pointer' : 'not-allowed',
});
const drawWrapS: CSSProperties = { padding: '16px 26px 22px', display: 'flex', justifyContent: 'center' };
const legalS: CSSProperties = { margin: '14px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.5 };

// ── Component ────────────────────────────────────────────────────────────────

export interface AdoptSignatureModalProps {
  signerName: string;
  /** Preselected tab — follows the current step's captureMode. */
  defaultTab?: AdoptMode;
  /**
   * May this signer TYPE their signature (pick a cursive style), or must they draw it?
   *
   * Bill 2026-07-16: internal users — FSRs and officers — must use the touchscreen
   * capture. A typed name in a chosen font is not an acceptable officer signature,
   * because the bank's own mark on the bank's own document should be that person's
   * actual hand, not a font selection anyone could reproduce from a name.
   *
   * Defaults to TRUE (customers keep both options). Officer surfaces pass false, which
   * removes the tab entirely rather than merely defaulting away from it — a hidden-but-
   * reachable typed tab is the same as no rule at all.
   */
  allowTyped?: boolean;
  /** Parent is submitting the adopted signature — lock the controls. */
  busy?: boolean;
  /** Signer adopted — parent stores it and signs the current step. */
  onAdopt: (adopted: AdoptedSignature) => void;
  /** Signer dismissed the adoption without adopting. */
  onCancel: () => void;
}

export function AdoptSignatureModal(props: AdoptSignatureModalProps): JSX.Element {
  const { signerName, defaultTab = 'drawn', allowTyped = true, busy, onAdopt, onCancel } = props;

  // A drawn-only signer can never land on the typed tab, even if the caller passed
  // defaultTab='typed' (the step's captureMode is about the DOCUMENT, not about who is
  // allowed to type).
  const [tab, setTab] = useState<AdoptMode>(allowTyped ? defaultTab : 'drawn');
  const [typedName, setTypedName] = useState<string>(signerName.trim());
  const [styleId, setStyleId] = useState<string>(TYPED_STYLES[0]!.id);
  const [working, setWorking] = useState<boolean>(false);

  const trimmed = typedName.trim();
  const typedValid = trimmed.length >= 2;
  const chosenStyle = useMemo(
    () => TYPED_STYLES.find((s) => s.id === styleId) ?? TYPED_STYLES[0]!,
    [styleId]
  );

  // Draw path — DrawnSignatureCapture already produced the raster + vector.
  function handleDrawn(result: SignatureCaptureResult): void {
    onAdopt({
      mode: 'drawn',
      typedName: signerName.trim() || (result.typedName ?? ''),
      svg: result.svgPath ?? '',
      pngBase64: result.pngBase64 ?? '',
    });
  }

  // Type path — build the vector + raster for the chosen cursive style.
  async function handleTypedAdopt(): Promise<void> {
    if (!typedValid || busy || working) return;
    setWorking(true);
    try {
      const svg = buildTypedSvg(trimmed, chosenStyle.svgStack);
      const pngBase64 = await rasterizeTypedPng(trimmed, chosenStyle.font);
      onAdopt({ mode: 'typed', typedName: trimmed, style: chosenStyle.id, svg, pngBase64 });
    } finally {
      setWorking(false);
    }
  }

  return (
    <div style={overlayS} role="dialog" aria-modal="true" aria-label="Adopt your signature">
      <div style={cardS}>
        <div style={headS}>
          <h1 style={titleS}>Adopt your signature</h1>
          <p style={subS}>
            {signerName ? `${signerName}, choose` : 'Choose'} how you’d like to sign. You’ll set
            this up once — then a single tap applies it to every document.
          </p>
        </div>

        {/* Drawn-only signers (FSRs/officers) get no tab bar at all — one mode, no
            choice to make. Rendering a disabled "Type it" would just invite the question. */}
        {allowTyped && (
          <div style={tabsRowS}>
            <button
              type="button"
              style={tabBtnS(tab === 'drawn')}
              onClick={() => setTab('drawn')}
              disabled={busy}
            >
              Draw it
            </button>
            <button
              type="button"
              style={tabBtnS(tab === 'typed')}
              onClick={() => setTab('typed')}
              disabled={busy}
            >
              Type it
            </button>
          </div>
        )}

        {tab === 'drawn' || !allowTyped ? (
          <div style={drawWrapS}>
            <DrawnSignatureCapture
              signerName={signerName}
              documentTitle="your documents"
              isTouchOnly
              disabled={busy}
              onCapture={handleDrawn}
              onCancel={onCancel}
            />
          </div>
        ) : (
          <>
            <div style={bodyPadS}>
              <label htmlFor="adopt-typed-name" style={fieldLabelS}>
                Your full legal name
              </label>
              <input
                id="adopt-typed-name"
                type="text"
                autoComplete="off"
                spellCheck={false}
                maxLength={120}
                style={inputS}
                value={typedName}
                disabled={busy || working}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder={signerName || 'Your full name'}
              />

              <div style={styleGridS}>
                {TYPED_STYLES.map((s) => {
                  const active = s.id === styleId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      style={styleCardS(active)}
                      aria-pressed={active}
                      onClick={() => setStyleId(s.id)}
                      disabled={busy || working}
                    >
                      <span style={styleTagS(active)}>
                        {active ? '✓ Selected style' : 'Choose this style'}
                      </span>
                      <span style={stylePreviewS(s.font)}>
                        {trimmed || signerName || 'Your name'}
                      </span>
                    </button>
                  );
                })}
              </div>

              <p style={legalS}>
                By adopting a signature you agree it is the electronic representation of your
                signature, with the same legal effect as signing with a pen.
              </p>
            </div>

            <div style={footerS}>
              <button type="button" style={ghostBtnS} onClick={onCancel} disabled={busy || working}>
                Cancel
              </button>
              <button
                type="button"
                style={primaryBtnS(typedValid && !busy && !working)}
                disabled={!typedValid || busy || working}
                onClick={() => void handleTypedAdopt()}
              >
                {busy || working ? 'Applying…' : 'Adopt & sign'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdoptSignatureModal;
